import React from 'react';
import { useState, useEffect, useRef } from 'react';
import { X, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { openSupport } from '../support';

/**
 * Milliseconds until the next midnight on the VIEWER'S OWN clock.
 *
 * Day overflow is normalised by the Date constructor, so the 31st, 31 Dec and
 * 29 Feb need no special case. The fields are wall-clock fields, so on a DST
 * day the gap is genuinely 23h or 25h - which is the truth the banner should
 * tell. A UTC version, or now + 86400e3, is wrong on exactly those days.
 *
 * The deadline is "midnight tonight" - real, checkable against the merchant's
 * own clock, the same for everyone, and it renews itself every day, so "today
 * only" is literally true. Deliberately NOT a 24h window stamped into
 * localStorage on first view: that gives every merchant a private deadline for
 * an offer that has none, and clearing site data buys a fresh 24 hours.
 */
function msUntilLocalMidnight(now) {
  const nextMidnight = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + 1,
    0, 0, 0, 0,
  );
  return nextMidnight.getTime() - now.getTime();
}

/**
 * The same value as a whole number of seconds, which is what the readout shows.
 *
 * ceil, not floor: through the last 999ms of the day floor yields 0 and the
 * readout shows 00:00:00 - an expired-looking clock on a live offer. ceil makes
 * 00:00:00 unreachable; the lowest value that can ever render is 00:00:01.
 * The max(1, ...) covers only a system clock moved backwards mid-tick.
 *
 * Seconds rather than ms is also what keeps the re-render honest: two ticks
 * inside the same second are the same integer, so useState bails out.
 */
function secondsUntilLocalMidnight() {
  return Math.max(1, Math.ceil(msUntilLocalMidnight(new Date()) / 1000));
}

/** Two-digit hours, minutes and seconds for the readout. */
function splitCountdown(totalSeconds) {
  const pad = (n) => String(n).padStart(2, '0');
  return {
    hours: pad(Math.floor(totalSeconds / 3600)),
    minutes: pad(Math.floor((totalSeconds % 3600) / 60)),
    seconds: pad(totalSeconds % 60),
  };
}

function useSecondsUntilMidnight() {
  const [secondsLeft, setSecondsLeft] = useState(() => secondsUntilLocalMidnight());

  useEffect(() => {
    let timeoutId = 0;

    // Every tick re-reads the clock instead of subtracting one, so a slept
    // laptop, a throttled background tab, a DST shift and a corrected system
    // clock all self-heal on the next tick rather than accumulating error.
    const tick = () => {
      setSecondsLeft(secondsUntilLocalMidnight());
      // Re-aim at 30ms past the NEXT whole second instead of adding a flat
      // 1000ms: a chain of 1000ms timeouts drifts by each callback's own cost
      // and eventually skips a displayed value. Late is safe here, early is
      // not - with ceil() a tick landing before the boundary shows the same
      // second twice. Local midnight always falls on a whole second, so the
      // countdown's boundaries coincide with the wall clock's.
      timeoutId = window.setTimeout(tick, 1030 - (Date.now() % 1000));
    };

    const stop = () => window.clearTimeout(timeoutId);
    const start = () => { stop(); tick(); };

    // Nothing to animate while the merchant is on another admin tab. Stopping
    // costs nothing, and the resync on return is exact because tick() reads
    // the clock - six slept hours, or a bfcache restore, land on the right
    // value immediately instead of showing a stale digit for up to a second.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };

    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Timer and listener are both owned by the effect that created them.
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  return secondsLeft;
}

/**
 * The shift readout: a split-flap plate, not a row of timer chips.
 *
 * This app IS a time clock - staff punch in and out on it - so the deadline is
 * shown on the instrument the merchant already knows rather than on generic SaaS
 * promo furniture. Mint digits on near-black is what a wall-mounted punch clock
 * looks like from across a stockroom, and the 1px seam across the middle is what
 * says the glyphs are printed on hinged cards rather than drawn on glass. The
 * seam is one CSS declaration: no image, no SVG, no animation.
 *
 * It does NOT flip. A display that actually moved would be charming for four
 * seconds and would then pull the eye off the staff table 3,600 times an hour.
 *
 * This is also the ONLY thing on the page that re-renders every second, and it
 * is a leaf holding two spans. The rail, headline, subline, CTA and dismiss are
 * all siblings and never reconcile on a tick. Do not lift this state into
 * AllotStaff, and never into StaffManagement, which would reconcile the staff
 * table 86,400 times a day.
 */
function ShiftReadout() {
  const { t } = useTranslation();
  const { hours, minutes, seconds } = splitCountdown(useSecondsUntilMidnight());

  return (
    // role="timer" carries an implicit aria-live="off", and the label is STATIC
    // on purpose: a label that changed with the digits would have a screen
    // reader announce the clock once a second.
    <div className="zs-offer-plate" role="timer" aria-label={t('offer_ends_midnight')}>
      <span className="zs-offer-plate-label">{t('offer_plate_label')}</span>
      <span className="zs-offer-digits">{`${hours}:${minutes}:${seconds}`}</span>
    </div>
  );
}

function AllotStaff({ onReady, seatCap, seatsUsed, planName, isLimitReached }) {
  const { t } = useTranslation();
  const [isDismissed, setIsDismissed] = useState(() => {
    try {
      return localStorage.getItem('allotStaffBannerDismissed') === 'true';
    } catch {
      // Private windows and blocked site data throw on access rather than
      // returning null, and a banner that cannot read its own dismissal should
      // still render rather than take the page down with it.
      return false;
    }
  });
  const hasNotifiedRef = useRef(false);

  useEffect(() => {
    if (hasNotifiedRef.current) return;
    hasNotifiedRef.current = true;
    if (typeof onReady === 'function') onReady();
  }, [onReady]);

  const handleGrabItNow = () => {
    // openSupport, not a direct window.$crisp push.
    //
    // The Shopify copy guards both pushes, so with Crisp blocked by an ad
    // blocker or a failed CDN the click is swallowed and nothing happens - the
    // merchant taps "Claim my seat" on an offer that then ignores them.
    // openSupport opens the chat with this exact message when it can and falls
    // back to a pre-filled mail when it cannot, so the offer always reaches
    // someone who can honour it.
    openSupport(t('yes_id_love_to_get_1_free_bonus_staff_slot'));
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    try {
      localStorage.setItem('allotStaffBannerDismissed', 'true');
    } catch {
      // Same story as the read: the dismissal simply will not persist across
      // reloads, which is far better than throwing out of a click.
    }
  };

  const resetBanner = () => {
    localStorage.removeItem('allotStaffBannerDismissed');
    setIsDismissed(false);
  };

  if (typeof window !== 'undefined') {
    window.resetAllotStaffBanner = resetBanner;
  }

  if (isDismissed) {
    return null;
  }

  // An extra seat is worth nothing to a shop that already has infinite seats,
  // and an offer worth nothing is exactly the hollow promo this redesign exists
  // to escape.
  const capIsUnlimited = typeof seatCap === 'string'
    && seatCap.trim().toLowerCase() === 'unlimited';
  if (capIsUnlimited) {
    return null;
  }

  // THE GUARD THAT MATTERS. StaffManagement seeds shopInfo as
  // { paid_available_staffs: 0, total_staff: 0, plan_id: null }, and
  // isStaffLimitReached() walks that to `0 >= 0` and returns TRUE - so without
  // this, every merchant on the platform sees "Staff limit reached" flash for
  // the length of the /getStaff round-trip, including shops with seats to
  // spare. A false statement on screen is not a cosmetic bug.
  const cap = Number(seatCap);
  const used = Number(seatsUsed);
  const loaded = Boolean(planName) && cap > 0;

  // Only the headline changes between states - the readout, subline, CTA and
  // dismiss are identical - so there is one conditional here and no branching
  // layout to keep in sync.
  let headline = t('offer_head_default');
  if (loaded && isLimitReached) {
    // Deliberately the same opening words as the plan card ~200px below, which
    // renders t('staff_limit_reached') at 100% used. The merchant has just been
    // stopped by it; echoing it says "yes, that thing, and it is fixable".
    headline = t('offer_head_blocked');
  } else if (loaded && cap >= 2 && used === cap - 1) {
    // Their own remaining headroom, which is verifiable one card down. The SEAT
    // is not scarce and this must never imply it is.
    headline = t('offer_head_last_seat');
  }

  return (
    <div className="zs-offer">
      {/* The punched edge of a timecard stub. Decorative only. */}
      <span className="zs-offer-rail" aria-hidden="true" />

      <button
        type="button"
        onClick={handleDismiss}
        className="zs-offer-x"
        aria-label={t('dismiss_offer')}
      >
        <X size={14} strokeWidth={2} aria-hidden="true" />
      </button>

      <div className="zs-offer-row">
        <ShiftReadout />

        <div className="zs-offer-copy">
          {/* h2, not h1: StaffManagement.jsx already renders the page title as
              <Text as="h1" variant="headingLg">. */}
          <h2 className="zs-offer-head">{headline}</h2>
          {/* Four objections in twelve words, in the order merchants raise
              them: effort, time, payment, billing change. All four checkable. */}
          <p className="zs-offer-sub">{t('offer_sub')}</p>
        </div>

        <button type="button" onClick={handleGrabItNow} className="zs-offer-cta">
          {/* The glyph is the only warning that a chat window is about to open.
              A surprise chat window is a trust cost, so keep it. */}
          <MessageSquare size={14} strokeWidth={2} aria-hidden="true" />
          {t('offer_cta_claim')}
        </button>
      </div>
    </div>
  );
}

export default AllotStaff;
